import React, { useEffect, useState } from "react"
import { get, remove } from "../../utils/apiClient"
import toast from "react-hot-toast"
import QueuePermissionModal from "./queuePermissionModal"
import Select from "react-select"
import Pagination from "../common/pagination"
import { SquarePen, Trash2, FileSpreadsheet } from "lucide-react"
import { displayNetIdEmail } from "../../utils/common"
import { ToolTip } from "../common/toolTip"

export default function QueuePermissionsAssociation({ authUser }) {
  const [records, setRecords] = useState([])
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editData, setEditData] = useState(null)
  const [queueToDelete, setQueueToDelete] = useState(null)
  const [queuesForSelect, setQueuesForSelect] = useState([])
  const [activeRowId, setActiveRowId] = useState(null)

  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  // for filters
  const [filteredRecords, setFilteredRecords] = useState([])
  const [selectedNetId, setSelectedNetId] = useState(null)
  const [selectedQueue, setSelectedQueue] = useState(null)
  const [selectedRole, setSelectedRole] = useState(null)

  const startIdx = (currentPage - 1) * rowsPerPage
  const currentRecords = filteredRecords.slice(startIdx, startIdx + rowsPerPage)

  const fetchList = async () => {
    try {
      const data = await get("/manage/queue/netId/with-permissions")
      setRecords(data.queueLevelNetIdPermissions || [])

      setQueuesForSelect(data.ownerQueues)
    } catch {
      toast.error("Failed to load queue permissions list")
    }
    setLoading(false)
  }

  const loadList = async () => {
    setLoading(true)
    await fetchList()
    setLoading(false)
  }

  useEffect(() => {
    loadList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleAdd = () => {
    setEditData(null)
    setModalOpen(true)
  }

  const handleEdit = (record) => {
    setEditData(record)
    setModalOpen(true)
  }

  const handleDelete = async (record) => {
    setActionLoading(true)

    try {
      await remove(`/manage/${record.queue}/${record.netId}/with-permissions`)

      toast.success("Deleted successfully")
      fetchList()
      setActionLoading(false)
      setQueueToDelete(null)
    } catch {
      setActionLoading(false)
      setQueueToDelete(null)
      toast.error("Delete failed")
    }
  }

  const handleModalSaved = () => {
    setModalOpen(false)
    fetchList()
  }

  const handleExportCsv = () => {
    const headers = ["Net ID", "Queue", "Role"]
    // Build rows
    const rows = filteredRecords.map((rec) => [
      displayNetIdEmail(rec.netId, rec.emailAddress),
      rec.queue,
      rec.role,
    ])
    // Assemble CSV string
    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map(
            (cell) =>
              // escape quotes
              `"${String(cell).replace(/"/g, '""')}"`,
          )
          .join(","),
      )
      .join("\n")
    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `queue_netid${selectedQueue?.value ? `_${selectedQueue?.value?.slice(0, 20).replace(/\s/g, "_")}` : ""}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const fetchFaxNumbers = async (queueName) => {
    try {
      const response = await get(
        `/admin/queue-fax-numbers?queueName=${queueName}`,
      )
      return `${response.length > 0 ? response?.join(", ") : "N/A"}`
    } catch (error) {
      console.error("Failed to fetch", error)
    }
  }

  // for filters
  useEffect(() => {
    let filtered = [...records]

    if (selectedNetId) {
      filtered = filtered.filter((rec) => rec.netId === selectedNetId.value)
    }

    if (selectedQueue) {
      filtered = filtered.filter((rec) => rec.queue === selectedQueue.value)
    }

    if (selectedRole) {
      filtered = filtered.filter((rec) => rec.role === selectedRole.value)
    }

    setFilteredRecords(filtered)
  }, [selectedNetId, selectedQueue, selectedRole, records])

  // for filters
  const netIdOptions = [
    ...new Map(
      records.map((r) => [r.netId, displayNetIdEmail(r.netId, r.emailAddress)]),
    ).entries(),
  ]
    .map(([netId, label]) => ({
      value: netId,
      label: label,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))

  const queueOptions = [...new Set(records.map((r) => r.queue))]
    .map((q) => ({
      value: q,
      label: q,
    }))
    .sort((a, b) => a.value.localeCompare(b.value))

  const roleOptions = [...new Set(records.map((r) => r.role))]
    .map((r) => ({
      value: r,
      label: r,
    }))
    .sort((a, b) => a.value.localeCompare(b.value))

  const showAddButton =
    queuesForSelect.length > 0 || authUser.loggedInUserType === "IT"
      ? true
      : queuesForSelect.length > 0
        ? true
        : false

  return (
    <div style={{ display: "flex", justifyContent: "center" }}>
      {loading ? (
        <div className="loading-container-fax-list">
          <div className="loading-message">Loading...</div>
        </div>
      ) : (
        <div
          className="faxes-content"
          style={{ marginTop: "20px", width: "100%" }}
        >
          {/* <button onClick={handleAdd} style={{ marginBottom: "10px" }}>
            Add
          </button> */}

          <div
            className="search-container"
            style={{
              width: "100%",
              alignItems: "end",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <div>
              {/* <div>
                <input
                  type="text"
                  placeholder={`Search NetIds`}
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value)
                    setCurrentPage(1)
                  }}
                />
              </div> */}
              <div>
                <div className="setting-note" style={{ paddingBottom: "10px" }}>
                  {authUser.loggedInUserType === "USER" ? (
                    <>
                      {queuesForSelect.length === 0 &&
                        `You don’t currently have any queues assigned to you as a MANAGER.`}

                      {records.length === 0 &&
                        queuesForSelect.length > 0 &&
                        `You are the sole Manager of this Queue. No other NetIds are currently associated. Use “Add Queue Permission” to assign roles and permissions to other NetIds.`}
                      {records.length > 0 &&
                        queuesForSelect.length > 0 &&
                        `Manage Queue-level associations for NetIds. You can update their roles and permissions for the Queues you own.`}
                    </>
                  ) : (
                    `Manage Queue-level associations for NetIds. You can update roles and permissions for the Queues`
                  )}
                </div>

                {(queuesForSelect.length > 0 || records.length > 0) && (
                  <div style={{ display: "flex", gap: "1rem" }}>
                    <Select
                      className="basic-single"
                      classNamePrefix="select"
                      placeholder="Filter by Net ID"
                      options={netIdOptions}
                      value={selectedNetId}
                      onChange={setSelectedNetId}
                      isClearable
                      isSearchable
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderRadius: 0,
                          minWidth: 250,
                        }),
                      }}
                      menuPortalTarget={document.body}
                    />
                    <Select
                      className="basic-single"
                      placeholder="Filter by Queue"
                      options={queueOptions}
                      value={selectedQueue}
                      onChange={setSelectedQueue}
                      isClearable
                      isSearchable
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderRadius: 0,
                          minWidth: 150,
                        }),
                      }}
                      menuPortalTarget={document.body}
                    />
                    <Select
                      className="basic-single"
                      placeholder="Filter by Role"
                      options={roleOptions}
                      value={selectedRole}
                      onChange={setSelectedRole}
                      isClearable
                      isSearchable
                      styles={{
                        control: (base) => ({
                          ...base,
                          borderRadius: 0,
                          minWidth: 100,
                        }),
                      }}
                    />
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setSelectedNetId(null)
                        setSelectedQueue(null)
                        setSelectedRole(null)
                      }}
                      title="Reset"
                    >
                      {" "}
                      ↺
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: "flex" }}>
              {currentRecords.length > 0 && (
                <button
                  className="faxes-button"
                  onClick={() => handleExportCsv()}
                  style={{ height: "28px" }}
                >
                  <span>
                    {" "}
                    <FileSpreadsheet size={16} color="white" />{" "}
                  </span>
                  <span>Export</span>
                </button>
              )}
              {showAddButton && (
                <button
                  className="faxes-button"
                  onClick={handleAdd}
                  style={{ marginBottom: "5px" }}
                >
                  + Queue Allocation to NetId
                </button>
              )}
            </div>
          </div>

          {(queuesForSelect.length > 0 || records.length > 0) && (
            <>
              <div className="table-container">
                <table className="faxes-table">
                  <thead>
                    <tr>
                      <th className="faxes-th">Net ID</th>
                      <th className="faxes-th">Queue</th>
                      <th className="faxes-th">Role</th>
                      {/* <th className="faxes-th">Permissions</th> */}
                      <th className="faxes-th" style={{ width: "50px" }}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentRecords.length > 0 ? (
                      <>
                        {currentRecords.map((rec) => (
                          <tr
                            key={rec.uuid}
                            className={
                              activeRowId &&
                              activeRowId === `${rec.netId}_${rec.queue}`
                                ? "fax-action-active"
                                : ""
                            }
                            onMouseEnter={() => {
                              if (
                                activeRowId &&
                                activeRowId !== `${rec.netId}_${rec.queue}`
                              )
                                setActiveRowId(null)
                            }}
                          >
                            <td className="faxes-td">
                              {displayNetIdEmail(rec.netId, rec.emailAddress)}
                            </td>
                            <td className="faxes-td">
                              <ToolTip
                                placement="top"
                                fetchTooltipContent={() =>
                                  fetchFaxNumbers(rec.queue)
                                }
                                title="Click To Load Queue Fax Numbers"
                              />
                              <span style={{ marginLeft: "4px" }}>
                                {rec.queue}
                              </span>
                            </td>
                            <td className="faxes-td">{rec.role}</td>
                            {/* <td className="faxes-td">
                      {Object.entries(rec.permissions).map(([cat, perms]) =>
                        perms.length ? (
                          <div key={cat}>
                            <b>{cat}:</b> {perms.join(", ")}
                          </div>
                        ) : null
                      )}
                    </td> */}
                            <td
                              className="faxes-td"
                              style={{ textAlign: "center" }}
                            >
                              {(queuesForSelect.includes(rec?.queue) ||
                                authUser.loggedInUserType === "IT") && (
                                <>
                                  <span
                                    style={{ cursor: "pointer" }}
                                    onClick={() => {
                                      setActiveRowId(
                                        `${rec.netId}_${rec.queue}`,
                                      )
                                      handleEdit(rec)
                                    }}
                                  >
                                    <SquarePen size="18" color="black" />
                                  </span>
                                  <span
                                    style={{
                                      cursor: "pointer",
                                      marginLeft: "10px",
                                    }}
                                    onClick={() => {
                                      setActiveRowId(
                                        `${rec.netId}_${rec.queue}`,
                                      )
                                      setQueueToDelete(rec)
                                    }}
                                  >
                                    <Trash2 size="18" color="red" />
                                  </span>
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                      </>
                    ) : (
                      <tr>
                        <td
                          colSpan="100%"
                          style={{
                            textAlign: "center",
                            padding: "10px",
                            border: "1px solid #ccc",
                          }}
                        >
                          No data found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {filteredRecords.length > 0 && (
                <div
                  className="faxes-pagination"
                  style={{
                    marginTop: "8px",
                    display: "flex",
                    justifyContent: "end",
                  }}
                >
                  {filteredRecords.length > 0 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={Math.ceil(
                        filteredRecords.length / rowsPerPage,
                      )}
                      rowsPerPage={rowsPerPage}
                      totalRows={filteredRecords.length}
                      onPageChange={(page) => setCurrentPage(page)}
                      onRowsPerPageChange={(count) => {
                        setRowsPerPage(count)
                        setCurrentPage(1) // Reset to first page on change
                      }}
                    />
                  )}
                </div>
              )}
            </>
          )}

          {modalOpen && (
            <QueuePermissionModal
              onClose={() => setModalOpen(false)}
              onSaved={handleModalSaved}
              editData={editData}
              queuesForSelect={queuesForSelect}
              authUser={authUser}
            />
          )}

          {queueToDelete && (
            <DeleteConfirmationPopup
              onConfirm={() => handleDelete(queueToDelete)}
              onCancel={() => setQueueToDelete(null)}
              actionLoading={actionLoading}
              netIdIdToDelete={queueToDelete}
            />
          )}
        </div>
      )}
    </div>
  )
}

const DeleteConfirmationPopup = ({
  onConfirm,
  onCancel,
  actionLoading,
  netIdIdToDelete,
}) => (
  <div className="popup-overlay">
    <div className="popup" style={{ padding: "20px", maxWidth: "700px" }}>
      {actionLoading ? (
        <div>Processing...</div>
      ) : (
        <>
          {/* <h3>Confirm Deletion</h3> */}
          <p>
            Are you share you want to delete{" "}
            <strong>{netIdIdToDelete?.queue}</strong> Queue level association
            for <strong>{netIdIdToDelete?.netId}?</strong>
          </p>
          {/* <p>⚠️ Deleting this netId will clean up all related associations.</p> */}
          <div className="popup-buttons">
            <button className="cancel" onClick={onCancel}>
              Cancel
            </button>
            <button className="delete" onClick={onConfirm}>
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  </div>
)
